import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { closeModal, openModal } from "../../redux/modal/modalActions";
import ResponseModal from "../common/modal/responseModal/ResponseModal";
import ConfirmationBox from "../common/confirmationBox/ConfirmationBox";
import Button from "../common/button/Button";
import TextField from "../common/textfield/TextField";
import SearchableSelect from "../common/searchableSelect/SearchableSelect";
import { getCookie } from "../../utils/cookies";
import { getPhasesByPropertyType } from "../../apis/phasesApis/getPhasesByPropertyType";
import { getContactsByProperty } from "../../apis/contactsApis/getContactsByProperty";
import { getAllTasksByProperty } from "../../apis/tasksApis/getAllTasksByProperty";
import { createTask } from "../../apis/tasksApis/createTask";
import { updateTask } from "../../apis/tasksApis/updateTask";
import { deleteTask } from "../../apis/tasksApis/deleteTask";
import Table from "../common/table/Table";
import {
  TableLinkCell,
  TableTextCell,
  TaskStatusCheckboxCell,
} from "../common/table/TableDisplayCells";
import {
  buildTaskStatusUpdatePayload,
  normalizeTaskType,
  validateTaskForStatusUpdate,
} from "../../helpers/taskUpdateHelper";
import {
  TASK_PRIORITY_EMPTY_LABEL,
  TASK_PRIORITY_SELECT_OPTIONS_WITH_EMPTY,
  getTaskPrioritySortRank,
  resolveTaskPriorityPayload,
} from "../../utils/taskPriority";
import { openTaskDetailModal } from "./TaskDetailModalContent";
import { TaskPriorityChip } from "../common/taskPriority/TaskPriorityChip";
import {
  FREQUENCY_LABELS,
  frequencyLabel,
  subPhaseName,
  taskTypeLabel,
} from "./taskDetailLabels";
import {
  showTaskStatusErrorModal,
  showTaskStatusSuccessModal,
} from "../../helpers/taskStatusFeedback";
import { createMenuItem } from "../../helpers/menuHelper";
import { PROPERTY_TASKS_TABLE_EMPTY } from "../../consts/tableEmptyMessages";
import {
  TABLE_EMPTY_CELL,
  truncateTableText,
  formatPersonDisplay,
  formatCreatedByDisplay,
} from "../../utils/tableDisplayUtils";
import "./PropertyTasksSection.css";

const TASK_TYPE_OPTIONS = [
  { id: "single", name: "Single", value: "single" },
  { id: "recurring", name: "Recurring", value: "recurring" },
];

const FREQUENCY_ORDER = [
  "daily",
  "weekly",
  "bi_weekly",
  "monthly",
  "quarterly",
  "semi_annually",
  "annually",
];

function emptyDraft() {
  return {
    description: "",
    phase_id: "",
    sub_phase_id: "",
    location: "",
    assigned_to_contact_id: "",
    start_date: "",
    end_date: "",
    status: "not_completed",
    link: "",
    comment: "",
    task_type: "single",
    frequency: "",
    priority: "",
  };
}

function taskToForm(task) {
  const tt = normalizeTaskType(task.task_type);
  return {
    description: task.description ?? "",
    phase_id: task.phase?.id != null ? String(task.phase.id) : "",
    sub_phase_id: task.sub_phase?.id != null ? String(task.sub_phase.id) : "",
    location: task.location ?? "",
    assigned_to_contact_id:
      task.assigned_to?.id != null ? String(task.assigned_to.id) : "",
    start_date: task.start_date ?? "",
    end_date: task.end_date ?? "",
    status: task.status === "completed" ? "completed" : "not_completed",
    link: task.link ?? "",
    comment: task.comment ?? "",
    task_type: tt,
    frequency:
      tt === "recurring" && task.frequency != null && String(task.frequency).trim()
        ? String(task.frequency).trim()
        : "",
    priority: task.priority != null ? String(task.priority) : "",
  };
}

function contactDisplayName(contact) {
  const a = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
  if (a) return a;
  if (typeof contact.name === "string" && contact.name.trim())
    return contact.name.trim();
  return contact.email || `Contact #${contact.id}`;
}

/** Top-level phase rows from get-phases-by-property-type (each may include sub_phases). */
function normalizePhaseTreeList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (typeof raw === "object" && Array.isArray(raw.results)) return raw.results;
  return [];
}

/** Backend may nest ids (e.g. `phase`, `sub_phase`) — options must carry a real id or save validation fails. */
function phaseRowId(phase) {
  if (!phase || typeof phase !== "object") return null;
  const id = phase.id ?? phase.phase_id ?? phase.phase?.id;
  if (id === undefined || id === null || String(id).trim() === "") return null;
  return String(id);
}

function phaseRowName(phase) {
  if (!phase || typeof phase !== "object") return "";
  if (typeof phase.name === "string" && phase.name.trim()) return phase.name.trim();
  if (typeof phase.phase?.name === "string" && phase.phase.name.trim())
    return phase.phase.name.trim();
  return "";
}

function subPhaseRowId(sub) {
  if (!sub || typeof sub !== "object") return null;
  const id = sub.id ?? sub.sub_phase_id ?? sub.sub_phase?.id;
  if (id === undefined || id === null || String(id).trim() === "") return null;
  return String(id);
}

function toFiniteNumberOrNull(raw) {
  const t = String(raw ?? "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function normalizeOptionalDateString(raw) {
  return typeof raw === "string" ? raw.trim() : "";
}

function validateComposer(form) {
  if (!form.task_type || !["single", "recurring"].includes(form.task_type))
    return "Task type is required.";
  const phaseId = String(form.phase_id ?? "").trim();
  const subId = String(form.sub_phase_id ?? "").trim();
  const start = normalizeOptionalDateString(form.start_date);
  const assigneeId = String(form.assigned_to_contact_id ?? "").trim();

  if (!phaseId) return "Phase is required.";
  if (!subId) return "Sub-phase is required.";
  if (!start) return "Start date is required.";
  if (!assigneeId) return "Assigned to is required.";
  if (toFiniteNumberOrNull(phaseId) == null)
    return "Phase is invalid. Re-select phase from the list.";
  if (toFiniteNumberOrNull(subId) == null)
    return "Sub-phase is invalid. Re-select sub-phase from the list.";
  if (toFiniteNumberOrNull(assigneeId) == null)
    return "Assigned to is invalid. Re-select a contact from the list.";

  const end = normalizeOptionalDateString(form.end_date);
  if (end && start && end < start)
    return "End date cannot be before start date.";
  return null;
}

/** Create / edit task form — Redux GenericModal, same chrome as TaskDetailModalContent */
export function TaskComposerModalContent({
  dispatch,
  mode,
  task,
  propertyId,
  propertyTypeId,
  refetchTasks,
  setBusyTaskId,
}) {
  const uid = getCookie("userId");
  const contactsLoadedRef = useRef(false);
  const [propertyContacts, setPropertyContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [phases, setPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(true);

  const isEdit = mode === "edit";
  const [form, setForm] = useState(() =>
    isEdit && task ? taskToForm(task) : emptyDraft(),
  );
  const [submitting, setSubmitting] = useState(false);

  const showErr = useCallback(
    (text) => {
      dispatch(openModal(<ResponseModal text={text} isError={true} />));
    },
    [dispatch],
  );

  const ensureContactsLoaded = useCallback(async () => {
    if (contactsLoadedRef.current) return;
    setContactsLoading(true);
    try {
      const res = await getContactsByProperty(propertyId);
      if (res.error_code === 1) {
        const list = res.res?.data;
        setPropertyContacts(Array.isArray(list) ? list : []);
        contactsLoadedRef.current = true;
      } else if (res.error_code === 3) {
        showErr(res.error_message ?? "Could not load project contacts.");
        setPropertyContacts([]);
      } else {
        setPropertyContacts([]);
        contactsLoadedRef.current = true;
      }
    } finally {
      setContactsLoading(false);
    }
  }, [propertyId, showErr]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (propertyTypeId == null || Number.isNaN(Number(propertyTypeId))) {
        setPhases([]);
        setPhasesLoading(false);
        return;
      }
      setPhasesLoading(true);
      const res = await getPhasesByPropertyType(Number(propertyTypeId));
      if (cancelled) return;
      if (res.error_code === 2 || res.error_code === 3) {
        showErr(
          res.error_message ?? "Could not load phases for this project type.",
        );
        setPhases([]);
      } else {
        setPhases(normalizePhaseTreeList(res.res?.data ?? res.res));
      }
      setPhasesLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [propertyTypeId, showErr]);

  useEffect(() => {
    void ensureContactsLoaded();
  }, [ensureContactsLoaded]);

  const subs = useMemo(() => {
    if (!form.phase_id || !Array.isArray(phases) || phases.length === 0)
      return [];
    const phase = phases.find((p) => phaseRowId(p) === String(form.phase_id));
    const sp = phase?.sub_phases ?? [];
    return Array.isArray(sp) ? sp : [];
  }, [form.phase_id, phases]);

  const phaseSearchOptions = useMemo(() => {
    if (!Array.isArray(phases) || phases.length === 0) return [];
    return phases
      .map((p, i) => {
        const pid = phaseRowId(p);
        if (pid == null) return null;
        const label = phaseRowName(p);
        return {
          id: pid,
          name: label || `Phase ${pid}`,
          value: String(i),
        };
      })
      .filter(Boolean);
  }, [phases]);

  const contactSearchOptions = useMemo(() => {
    if (!Array.isArray(propertyContacts) || propertyContacts.length === 0)
      return [];
    return propertyContacts.map((c, i) => ({
      id: c.id,
      name: contactDisplayName(c),
      value: String(i),
    }));
  }, [propertyContacts]);

  const frequencySearchOptions = useMemo(
    () =>
      FREQUENCY_ORDER.map((key) => ({
        id: key,
        name: FREQUENCY_LABELS[key],
        value: key,
      })),
    [],
  );

  const buildCreateBody = () => {
    const endTrim = normalizeOptionalDateString(form.end_date);
    const body = {
      property_id: Number(propertyId),
      phase_id: toFiniteNumberOrNull(form.phase_id),
      sub_phase_id: toFiniteNumberOrNull(form.sub_phase_id),
      start_date: form.start_date,
      end_date: endTrim || null,
      created_by_user_id: Number(uid),
      assigned_by_user_id: Number(uid),
      status: form.status,
      assigned_to_contact_id: toFiniteNumberOrNull(form.assigned_to_contact_id),
      task_type: form.task_type,
      frequency:
        form.task_type === "recurring" && form.frequency.trim()
          ? form.frequency.trim()
          : null,
    };
    const loc = form.location.trim();
    if (loc) body.location = loc;
    const desc = form.description.trim();
    if (desc) body.description = desc;
    const link = form.link.trim();
    if (link) body.link = link;
    const comment = form.comment.trim();
    if (comment) body.comment = comment;
    body.priority = resolveTaskPriorityPayload(form.priority);
    return body;
  };

  const handleSave = async () => {
    const err = validateComposer(form);
    if (err) {
      showErr(err);
      return;
    }
    if (!uid || Number.isNaN(Number(uid))) {
      showErr("Missing session user ID. Sign in again.");
      return;
    }

    const saveDisabled = submitting || phasesLoading;
    if (saveDisabled) return;

    setSubmitting(true);

    try {
      if (!isEdit) {
        const res = await createTask(buildCreateBody());
        if (res.error_code !== 1) {
          showErr(res.error_message ?? "Could not create task.");
          return;
        }
        dispatch(closeModal());
        await refetchTasks({ showSpinner: false });
        return;
      }

      const taskSnap = task;
      if (!taskSnap?.id) {
        showErr("Could not determine which task to update.");
        return;
      }

      const endTrim = normalizeOptionalDateString(form.end_date);
      const payload = {
        task_id: taskSnap.id,
        property_id: Number(propertyId),
        phase_id: toFiniteNumberOrNull(form.phase_id),
        sub_phase_id: toFiniteNumberOrNull(form.sub_phase_id),
        start_date: form.start_date,
        end_date: endTrim || null,
        updated_by_user_id: Number(uid),
        location: form.location.trim() || null,
        description: form.description.trim() || null,
        status: form.status,
        link: form.link.trim() || null,
        comment: form.comment.trim() || null,
        assigned_to_contact_id: toFiniteNumberOrNull(
          form.assigned_to_contact_id,
        ),
        task_type: form.task_type,
        frequency:
          form.task_type === "recurring" && form.frequency.trim()
            ? form.frequency.trim()
            : null,
        priority: resolveTaskPriorityPayload(form.priority),
      };

      setBusyTaskId(taskSnap.id);
      const res = await updateTask(payload);
      if (res.error_code !== 1) {
        showErr(res.error_message ?? "Could not update task.");
        return;
      }
      dispatch(closeModal());
      await refetchTasks({ showSpinner: false });
    } finally {
      setSubmitting(false);
      if (isEdit) setBusyTaskId(null);
    }
  };

  const subSearchOptions = Array.isArray(subs)
    ? subs
        .map((s, i) => {
          const sid = subPhaseRowId(s);
          if (sid == null) return null;
          return {
            id: sid,
            name: subPhaseName(s) || `#${sid}`,
            value: String(i),
          };
        })
        .filter(Boolean)
    : [];

  const selectedPhaseName =
    phaseRowName(
      phases.find((p) => phaseRowId(p) === String(form.phase_id)) ?? {},
    ) || "";
  const selSubRow =
    Array.isArray(subs) &&
    subs.find((s) => subPhaseRowId(s) === String(form.sub_phase_id));
  const selectedSubName = selSubRow
    ? subPhaseName(selSubRow) || `#${subPhaseRowId(selSubRow) ?? ""}`
    : "";
  const selContactRow =
    form.assigned_to_contact_id &&
    propertyContacts.find(
      (c) => String(c.id) === String(form.assigned_to_contact_id),
    );
  const assigneeDisplayName = selContactRow
    ? contactDisplayName(selContactRow)
    : "";

  const selectedTaskTypeName = taskTypeLabel(form.task_type);
  const selectedFrequencyName =
    form.task_type === "recurring" && form.frequency.trim()
      ? FREQUENCY_LABELS[form.frequency.trim()] ?? form.frequency.trim()
      : "";

  const selectedPriorityName =
    form.priority.trim() === ""
      ? TASK_PRIORITY_EMPTY_LABEL
      : TASK_PRIORITY_SELECT_OPTIONS_WITH_EMPTY.find(
          (o) => String(o.id) === String(form.priority),
        )?.name ?? TASK_PRIORITY_EMPTY_LABEL;

  const saveDisabled = submitting || phasesLoading;
  const subDisabled =
    saveDisabled ||
    phasesLoading ||
    !form.phase_id ||
    subSearchOptions.length === 0;

  const idSuffix = isEdit && task?.id != null ? `edit-${task.id}` : "create";

  const taskTypeSelectEl = (
    <SearchableSelect
      name={`tasks-type-${idSuffix}`}
      label="Task type"
      required={true}
      error=""
      disabled={saveDisabled}
      options={TASK_TYPE_OPTIONS}
      value={selectedTaskTypeName}
      onChange={(_e, newValue) => {
        const raw =
          newValue?.id !== undefined && String(newValue.id) !== ""
            ? String(newValue.id)
            : "single";
        const nextType = normalizeTaskType(raw);
        setForm((prev) => ({
          ...prev,
          task_type: nextType,
          frequency: nextType === "recurring" ? prev.frequency : "",
        }));
      }}
    />
  );

  return (
    <div className="property-tasks-mobile-detail property-tasks-mobile-detail--composer">
      <div className="property-tasks-mobile-detail-header">
        <h4 className="property-tasks-mobile-detail-title">
          {isEdit ? "Edit task" : "Add task"}
        </h4>
        <button
          type="button"
          className="property-tasks-mobile-detail-close"
          aria-label="Close"
          onClick={() => dispatch(closeModal())}
        >
          ×
        </button>
      </div>
      <div className="property-tasks-mobile-detail-body">
        <div className="property-tasks-composer-stack">
          <div className="property-tasks-form-grid">
            <div className="property-tasks-field-span-full">
              <TextField
                label="Description"
                name={`tasks-desc-${idSuffix}`}
                type="multilined"
                className="property-tasks-textfield property-tasks-long-input"
                placeHolder=""
                disabled={saveDisabled}
                value={form.description}
                setValue={(v) =>
                  setForm((prev) => ({ ...prev, description: v }))
                }
              />
            </div>

            <div className="property-tasks-field-pair">
              <SearchableSelect
                name={`tasks-phase-${idSuffix}`}
                label="Phase"
                required={true}
                error=""
                disabled={
                  saveDisabled ||
                  phasesLoading ||
                  (!phasesLoading && phaseSearchOptions.length === 0)
                }
                options={phaseSearchOptions}
                value={selectedPhaseName}
                onChange={(_e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    phase_id:
                      newValue?.id !== undefined && newValue?.id !== ""
                        ? String(newValue.id)
                        : "",
                    sub_phase_id: "",
                  }))
                }
              />
              <SearchableSelect
                name={`tasks-sub-${idSuffix}`}
                label="Sub-phase"
                required={true}
                error=""
                disabled={subDisabled}
                options={subSearchOptions}
                value={selectedSubName}
                onChange={(_e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    sub_phase_id:
                      newValue?.id !== undefined && newValue?.id !== ""
                        ? String(newValue.id)
                        : "",
                  }))
                }
              />
            </div>

            <div
              className={[
                "property-tasks-field-pair",
                "property-tasks-field-pair--type-frequency",
                form.task_type !== "recurring" &&
                  "property-tasks-field-pair--frequency-collapsed-mobile",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {taskTypeSelectEl}
              <div
                className={
                  form.task_type !== "recurring"
                    ? "property-tasks-frequency-slot-visually-hidden"
                    : undefined
                }
                aria-hidden={form.task_type !== "recurring"}
              >
                <SearchableSelect
                  name={`tasks-frequency-${idSuffix}`}
                  label="Frequency"
                  required={false}
                  error=""
                  disabled={saveDisabled || form.task_type !== "recurring"}
                  options={frequencySearchOptions}
                  value={selectedFrequencyName}
                  onChange={(_e, newValue) => {
                    setForm((prev) => ({
                      ...prev,
                      frequency:
                        newValue?.id !== undefined &&
                        String(newValue.id) !== ""
                          ? String(newValue.id)
                          : "",
                    }));
                  }}
                />
              </div>
            </div>

            <div className="property-tasks-field-pair property-tasks-field-pair--priority">
              <SearchableSelect
                name={`tasks-priority-${idSuffix}`}
                label="Priority"
                required={false}
                error=""
                disabled={saveDisabled}
                options={TASK_PRIORITY_SELECT_OPTIONS_WITH_EMPTY}
                value={selectedPriorityName}
                onChange={(_e, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    priority:
                      newValue?.id !== undefined && String(newValue.id) !== ""
                        ? String(newValue.id)
                        : "",
                  }));
                }}
              />
              <div className="property-tasks-priority-preview" aria-hidden="true">
                <span className="property-tasks-priority-preview-label">Preview</span>
                <TaskPriorityChip priority={form.priority || null} />
              </div>
            </div>

            <div className="property-tasks-field-pair property-tasks-field-pair--assign-location">
              <SearchableSelect
                name={`tasks-assign-${idSuffix}`}
                label="Assigned to"
                required={true}
                error=""
                disabled={saveDisabled || contactsLoading}
                options={contactSearchOptions}
                value={assigneeDisplayName}
                onDropdownVisibleChange={(open) => {
                  if (open) void ensureContactsLoaded();
                }}
                onChange={(_e, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    assigned_to_contact_id:
                      newValue?.id !== undefined && String(newValue.id) !== ""
                        ? String(newValue.id)
                        : "",
                  }));
                }}
              />
              <TextField
                label="Location"
                name={`tasks-loc-${idSuffix}`}
                type="text"
                className="property-tasks-textfield"
                disabled={saveDisabled}
                value={form.location}
                placeHolder=""
                setValue={(v) => setForm((prev) => ({ ...prev, location: v }))}
              />
            </div>

            <div className="property-tasks-field-pair property-tasks-field-pair--dates">
              <TextField
                label="Start date"
                name={`tasks-start-${idSuffix}`}
                type="date"
                className="property-tasks-textfield"
                isRequired={true}
                disabled={saveDisabled}
                value={form.start_date}
                setValue={(v) =>
                  setForm((prev) => ({ ...prev, start_date: v }))
                }
              />
              <TextField
                label="End date"
                name={`tasks-end-${idSuffix}`}
                type="date"
                min={form.start_date || undefined}
                className="property-tasks-textfield"
                disabled={saveDisabled}
                value={form.end_date}
                setValue={(v) => setForm((prev) => ({ ...prev, end_date: v }))}
              />
            </div>

            <div className="property-tasks-field-span-full">
              <TextField
                label="Link"
                name={`tasks-link-${idSuffix}`}
                type="text"
                className="property-tasks-textfield"
                disabled={saveDisabled}
                value={form.link}
                placeHolder=""
                setValue={(v) => setForm((prev) => ({ ...prev, link: v }))}
              />
            </div>
            <div className="property-tasks-field-span-full">
              <TextField
                label="Comment"
                name={`tasks-comment-${idSuffix}`}
                type="multilined"
                className="property-tasks-textfield property-tasks-long-input"
                disabled={saveDisabled}
                value={form.comment}
                placeHolder=""
                setValue={(v) => setForm((prev) => ({ ...prev, comment: v }))}
              />
            </div>

            <div className="property-tasks-field-span-full property-tasks-checkbox-row property-tasks-checkbox-row--composer-footer">
              <Checkbox
                size="small"
                checked={form.status === "completed"}
                disabled={saveDisabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    status: e.target.checked ? "completed" : "not_completed",
                  }))
                }
                sx={{ p: 0.25 }}
              />
              <span className="property-tasks-muted-label">Completed</span>
            </div>
          </div>
        </div>
      </div>
      <div className="property-tasks-mobile-detail-footer">
        <Button
          type="button"
          buttonText="Save"
          disabled={saveDisabled}
          className="btn-login property-tasks-mobile-detail-btn"
          onClick={() => void handleSave()}
        />
        <Button
          type="button"
          buttonText="Cancel"
          className="btn-login property-tasks-mobile-detail-btn property-tasks-mobile-detail-btn--secondary"
          onClick={() => dispatch(closeModal())}
        />
      </div>
    </div>
  );
}

TaskComposerModalContent.propTypes = {
  dispatch: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  task: PropTypes.object,
  propertyId: PropTypes.number.isRequired,
  propertyTypeId: PropTypes.number,
  refetchTasks: PropTypes.func.isRequired,
  setBusyTaskId: PropTypes.func.isRequired,
};

function mapTaskToTableRow(task) {
  return {
    id: task.id,
    task_type_display: taskTypeLabel(task.task_type),
    description:
      truncateTableText(task.description?.trim() ? task.description : "") ||
      TABLE_EMPTY_CELL,
    phase_name: task.phase?.name ?? "—",
    sub_phase_name: task.sub_phase
      ? subPhaseName(task.sub_phase) || "—"
      : "—",
    assigned_display: formatPersonDisplay(task.assigned_to),
    frequency_display: frequencyLabel(task.frequency),
    location_display: task.location?.trim() ? task.location : "—",
    start_date_display: task.start_date ?? "—",
    end_date_display: task.end_date ?? "—",
    days_passed_display:
      task.days_passed !== undefined && task.days_passed !== null
        ? String(task.days_passed)
        : "—",
    completed_sort: task.status === "completed" ? 1 : 0,
    priority_sort: getTaskPrioritySortRank(task.priority),
    link_sort: task.link?.trim() ? task.link : "—",
    comment_display:
      truncateTableText(task.comment?.trim() ? task.comment : "") ||
      TABLE_EMPTY_CELL,
    created_by_display: formatCreatedByDisplay(task.created_by, TABLE_EMPTY_CELL),
    _task: task,
  };
}

function buildPropertyTaskTableHeaders({
  includeFrequency,
  includeTaskType,
  openTaskDetailModal,
  busyTaskId,
  canUpdateTasks,
  handleStatusCheckbox,
}) {
  const statusCol = {
    key: "completed_sort",
    name: "Status",
    formatter: (row) => {
      const task = row._task;
      const busyRow = busyTaskId === task.id;
      return (
        <TaskStatusCheckboxCell
          task={task}
          checked={task.status === "completed"}
          disabled={!canUpdateTasks || busyRow}
          onChange={(e) => handleStatusCheckbox(task, e.target.checked)}
        />
      );
    },
  };

  const descriptionCol = {
    key: "description",
    name: "Descrip.",
    formatter: (row) => (
      <TableTextCell
        text={row._task.description}
        className="property-tasks-table-text-cell"
        wrapClassName="property-tasks-tooltip-wrap"
        tooltipContentClassName="property-tasks-table-tooltip-content"
        tooltipClassName="property-tasks-table-tooltip"
        buttonClassName="property-tasks-desc-btn"
        asButton
        onClick={(e) => {
          e.stopPropagation();
          openTaskDetailModal(row._task);
        }}
      />
    ),
  };

  const typeCol = includeTaskType
    ? [{ key: "task_type_display", name: "Type" }]
    : [];

  const assignedBlock = includeFrequency
    ? [
        { key: "assigned_display", name: "Assigned to" },
        {
          key: "frequency_display",
          name: "Frequency",
          formatter: (row) => row.frequency_display,
        },
      ]
    : [{ key: "assigned_display", name: "Assigned to" }];

  const linkCol = {
    key: "link_sort",
    name: "Link",
    formatter: (row) => (
      <TableLinkCell
        url={row._task.link}
        linkClassName="property-tasks-compact-link"
        onClick={(e) => e.stopPropagation()}
      />
    ),
  };

  const priorityCol = {
    key: "priority_sort",
    name: "Priority",
    formatter: (row) => (
      <TaskPriorityChip
        priority={row._task.priority}
        className="task-priority-chip--compact"
      />
    ),
  };

  return [
    statusCol,
    priorityCol,
    descriptionCol,
    { key: "phase_name", name: "Phase" },
    { key: "sub_phase_name", name: "Sub-p." },
    ...typeCol,
    ...assignedBlock,
    { key: "location_display", name: "Locat." },
    { key: "start_date_display", name: "S. Date" },
    { key: "end_date_display", name: "E. Date" },
    { key: "days_passed_display", name: "Days Passed." },
    linkCol,
    {
      key: "comment_display",
      name: "Comment",
      formatter: (row) => (
        <TableTextCell
          text={row._task.comment}
          className="property-tasks-table-text-cell property-tasks-table-text-cell--comment"
          wrapClassName="property-tasks-tooltip-wrap"
          tooltipContentClassName="property-tasks-table-tooltip-content"
          tooltipClassName="property-tasks-table-tooltip"
        />
      ),
    },
    { key: "created_by_display", name: "Created by" },
  ];
}

function PropertyTasksSection({
  propertyId,
  propertyTypeId,
  canCreateTasks,
  canUpdateTasks,
  canDeleteTasks,
  dispatch,
}) {
  const uid = getCookie("userId");

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  const [busyTaskId, setBusyTaskId] = useState(null);

  const showError = useCallback(
    (text) => {
      showTaskStatusErrorModal(dispatch, text);
    },
    [dispatch],
  );

  const showStatusSuccessToast = useCallback(
    (completedNow) => {
      showTaskStatusSuccessModal(dispatch, completedNow);
    },
    [dispatch],
  );

  /**
   * @param {{ showSpinner?: boolean }} [options] — pass `{ showSpinner: false }` after
   * mutations so the table stays mounted (no full-section flash); initial load keeps spinner.
   */
  const refetchTasks = useCallback(
    async (options) => {
      const showSpinner = options?.showSpinner !== false;
      if (!propertyId) return;
      if (showSpinner) setTasksLoading(true);
      try {
        const res = await getAllTasksByProperty(propertyId);
        if (res.error_code !== 1) {
          showError(
            res.error_message ?? "Could not load tasks for this project.",
          );
          setTasks([]);
          return;
        }
        const raw = res.res?.data;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setTasks(arr);
      } finally {
        if (showSpinner) setTasksLoading(false);
      }
    },
    [propertyId, showError],
  );

  useEffect(() => {
    refetchTasks();
  }, [propertyId, refetchTasks]);

  const openComposerModal = useCallback(
    (mode, taskSnap = null) => {
      const isCreate = mode === "create";
      if ((isCreate && !canCreateTasks) || (!isCreate && !canUpdateTasks)) {
        return;
      }
      dispatch(
        openModal(
          <TaskComposerModalContent
            dispatch={dispatch}
            mode={mode}
            task={taskSnap}
            propertyId={propertyId}
            propertyTypeId={propertyTypeId}
            refetchTasks={refetchTasks}
            setBusyTaskId={setBusyTaskId}
          />,
        ),
      );
    },
    [
      canCreateTasks,
      canUpdateTasks,
      dispatch,
      propertyId,
      propertyTypeId,
      refetchTasks,
      setBusyTaskId,
    ],
  );

  const handleStatusCheckbox = useCallback(
    async (task, checked) => {
      if (!canUpdateTasks) return;

      const validationError = validateTaskForStatusUpdate(task, propertyId);
      if (validationError) {
        showError(validationError);
        return;
      }

      const next = checked ? "completed" : "not_completed";
      if (!uid || Number.isNaN(Number(uid))) {
        showError("Missing session user ID. Sign in again.");
        return;
      }
      setBusyTaskId(task.id);
      try {
        const res = await updateTask(
          buildTaskStatusUpdatePayload(task, checked, uid, propertyId),
        );
        if (res.error_code !== 1) {
          showError(res.error_message ?? "Could not update status.");
          return;
        }
        await refetchTasks({ showSpinner: false });
        dispatch(closeModal());
        showStatusSuccessToast(next === "completed");
      } finally {
        setBusyTaskId(null);
      }
    },
    [
      canUpdateTasks,
      dispatch,
      propertyId,
      refetchTasks,
      setBusyTaskId,
      showError,
      showStatusSuccessToast,
      uid,
    ],
  );

  const executeDeleteTask = useCallback(
    async (taskId) => {
      if (!canDeleteTasks) return;
      setBusyTaskId(taskId);
      try {
        const res = await deleteTask(taskId);
        if (res.error_code !== 1) {
          showError(res.error_message ?? "Could not delete task.");
          return;
        }
        await refetchTasks({ showSpinner: false });
        dispatch(closeModal());
      } finally {
        setBusyTaskId(null);
      }
    },
    [canDeleteTasks, dispatch, refetchTasks, setBusyTaskId, showError],
  );

  const confirmDeleteTask = useCallback(
    (taskId) => {
      if (!canDeleteTasks) return;
      dispatch(
        openModal(
          <ConfirmationBox
            msg="Are you sure you want to delete the task?"
            saveText="Delete"
            discardText="Cancel"
            saveAction={() => {
              void executeDeleteTask(taskId);
            }}
          />,
        ),
      );
    },
    [canDeleteTasks, dispatch, executeDeleteTask],
  );

  const openTaskDetailModalHandler = useCallback(
    (task) => {
      openTaskDetailModal(dispatch, {
        task,
        canUpdateTasks,
        canDeleteTasks,
        busyTaskId,
        onEdit: () => openComposerModal("edit", task),
        onConfirmDelete: () => confirmDeleteTask(task.id),
        onStatusCheckbox: handleStatusCheckbox,
      });
    },
    [
      dispatch,
      canUpdateTasks,
      canDeleteTasks,
      busyTaskId,
      openComposerModal,
      confirmDeleteTask,
      handleStatusCheckbox,
    ],
  );

  const isCompleted = (t) => t.status === "completed";

  const singleTasks = useMemo(
    () =>
      tasks.filter(
        (t) => normalizeTaskType(t.task_type) !== "recurring" && !isCompleted(t),
      ),
    [tasks],
  );

  const recurringTasks = useMemo(
    () =>
      tasks.filter(
        (t) => normalizeTaskType(t.task_type) === "recurring" && !isCompleted(t),
      ),
    [tasks],
  );

  const completedTasks = useMemo(() => tasks.filter(isCompleted), [tasks]);

  const singleTableRows = useMemo(
    () => singleTasks.map(mapTaskToTableRow),
    [singleTasks],
  );

  const recurringTableRows = useMemo(
    () => recurringTasks.map(mapTaskToTableRow),
    [recurringTasks],
  );

  const completedTableRows = useMemo(
    () => completedTasks.map(mapTaskToTableRow),
    [completedTasks],
  );

  const singleTaskHeaders = useMemo(
    () =>
      buildPropertyTaskTableHeaders({
        includeFrequency: false,
        openTaskDetailModal: openTaskDetailModalHandler,
        busyTaskId,
        canUpdateTasks,
        handleStatusCheckbox,
      }),
    [
      openTaskDetailModalHandler,
      busyTaskId,
      canUpdateTasks,
      handleStatusCheckbox,
    ],
  );

  const recurringTaskHeaders = useMemo(
    () =>
      buildPropertyTaskTableHeaders({
        includeFrequency: true,
        openTaskDetailModal: openTaskDetailModalHandler,
        busyTaskId,
        canUpdateTasks,
        handleStatusCheckbox,
      }),
    [
      openTaskDetailModalHandler,
      busyTaskId,
      canUpdateTasks,
      handleStatusCheckbox,
    ],
  );

  const completedTaskHeaders = useMemo(
    () =>
      buildPropertyTaskTableHeaders({
        includeFrequency: true,
        includeTaskType: true,
        openTaskDetailModal: openTaskDetailModalHandler,
        busyTaskId,
        canUpdateTasks,
        handleStatusCheckbox,
      }),
    [
      openTaskDetailModalHandler,
      busyTaskId,
      canUpdateTasks,
      handleStatusCheckbox,
    ],
  );

  const rowActions = useMemo(
    () => {
      const actions = [
        canUpdateTasks &&
          createMenuItem(
            "Edit",
            (row) => openComposerModal("edit", row._task),
            <EditIcon />,
          ),
        canDeleteTasks &&
          createMenuItem(
            "Delete",
            (row) => confirmDeleteTask(row.id),
            <DeleteIcon />,
          ),
      ].filter(Boolean);

      return actions.length > 0 ? actions : undefined;
    },
    [canDeleteTasks, canUpdateTasks, openComposerModal, confirmDeleteTask],
  );

  return (
    <div className="property-tasks-root">
      <h3 className="property-details-profile-notes-heading property-details-section-heading--tasks">
        <strong>Tasks:</strong>
      </h3>
      {tasksLoading && (
        <div className="property-tasks-spinner-wrap" role="status">
          <CircularProgress size={36} thickness={4} />
        </div>
      )}
      {!tasksLoading && (
        <>
          <h4 className="property-tasks-subsection-heading">Single tasks</h4>
          <div className="table-main-container-property-tasks">
            <Table
              searchable={false}
              tableHeight="450px"
              headers={singleTaskHeaders}
              rows={singleTableRows}
              rowActions={rowActions}
              emptyMessage={PROPERTY_TASKS_TABLE_EMPTY.SINGLE}
            />
          </div>
          <h4 className="property-tasks-subsection-heading property-tasks-subsection-heading--recurring">
            Recurring tasks
          </h4>
          <div className="table-main-container-property-tasks table-main-container-property-tasks--recurring">
            <Table
              searchable={false}
              tableHeight="450px"
              headers={recurringTaskHeaders}
              rows={recurringTableRows}
              rowActions={rowActions}
              emptyMessage={PROPERTY_TASKS_TABLE_EMPTY.RECURRING}
            />
          </div>
          <h4 className="property-tasks-subsection-heading property-tasks-subsection-heading--completed">
            Completed tasks
          </h4>
          <div className="table-main-container-property-tasks table-main-container-property-tasks--completed">
            <Table
              searchable={false}
              tableHeight="450px"
              headers={completedTaskHeaders}
              rows={completedTableRows}
              rowActions={rowActions}
              emptyMessage={PROPERTY_TASKS_TABLE_EMPTY.COMPLETED}
            />
          </div>
          {canCreateTasks && (
            <div className="property-tasks-add-task-wrap">
              <button
                type="button"
                className="image-btn property-tasks-add-task-btn"
                onClick={() => openComposerModal("create")}
              >
                Add Task
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

PropertyTasksSection.propTypes = {
  propertyId: PropTypes.number.isRequired,
  propertyTypeId: PropTypes.number,
  canCreateTasks: PropTypes.bool.isRequired,
  canUpdateTasks: PropTypes.bool.isRequired,
  canDeleteTasks: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default PropertyTasksSection;
